import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'

const PACGlobalConfigurations = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [validatingId, setValidatingId] = useState(null)
  
  // Filtros
  const [filterAmbiente, setFilterAmbiente] = useState('')
  const [filterActivo, setFilterActivo] = useState('')
  
  // Formulario
  const [formData, setFormData] = useState({
    nombre: '',
    ambiente: 'pruebas',
    usuario: '',
    password: '',
    url_timbrado: 'https://ws.multifacturas.com/api/',
    url_cancelacion: 'https://ws.multifacturas.com/api/',
    url_estatus: 'https://ws.multifacturas.com/api/',
    activo: true,
    notas: '',
  })

  // Verificar si es superadmin
  const isSuperAdmin = user?.is_superuser || false

  // Obtener configuraciones globales
  const { data: configsData, isLoading } = useQuery(
    ['pac-global-configs', filterAmbiente, filterActivo],
    async () => {
      const params = {}
      if (filterAmbiente) params.ambiente = filterAmbiente
      if (filterActivo !== '') params.activo = filterActivo === 'true'
      
      const response = await api.get('/financial/configuraciones-pac-global/', { params })
      return response.data.results || response.data || []
    },
    { enabled: !!user && isSuperAdmin }
  )

  const configs = configsData || []

  // Mutación para crear/actualizar
  const saveMutation = useMutation(
    async (data) => {
      if (editingConfig) {
        return api.put(`/financial/configuraciones-pac-global/${editingConfig.id}/`, data)
      } else {
        return api.post('/financial/configuraciones-pac-global/', data)
      }
    },
    {
      onSuccess: () => {
        toast.success(editingConfig ? 'Configuración global actualizada correctamente' : 'Configuración global creada correctamente')
        queryClient.invalidateQueries('pac-global-configs')
        queryClient.invalidateQueries('pac-configs') // Invalidar también las configs por cliente
        handleCloseModal()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.detail ||
                        (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : 'Error al guardar la configuración')
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para eliminar
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/financial/configuraciones-pac-global/${id}/`)
    },
    {
      onSuccess: () => {
        toast.success('Configuración global eliminada correctamente')
        queryClient.invalidateQueries('pac-global-configs')
        queryClient.invalidateQueries('pac-configs')
      },
      onError: () => {
        toast.error('Error al eliminar la configuración global')
      }
    }
  )

  // Mutación para validar credenciales
  const validateMutation = useMutation(
    async (id) => {
      const response = await api.post(`/financial/configuraciones-pac-global/${id}/validar_servicio/`)
      return response.data
    },
    {
      onSuccess: (data) => {
        if (data.valid) {
          toast.success('Credenciales válidas')
        } else {
          toast.warning('Las credenciales no son válidas')
        }
        setValidatingId(null)
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al validar credenciales'
        toast.error(errorMsg)
        setValidatingId(null)
      }
    }
  )

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config)
      setFormData({
        nombre: config.nombre || '',
        ambiente: config.ambiente || 'pruebas',
        usuario: config.usuario || '',
        password: '', // No mostramos la contraseña existente
        url_timbrado: config.url_timbrado || 'https://ws.multifacturas.com/api/',
        url_cancelacion: config.url_cancelacion || 'https://ws.multifacturas.com/api/',
        url_estatus: config.url_estatus || 'https://ws.multifacturas.com/api/',
        activo: config.activo !== undefined ? config.activo : true,
        notas: config.notas || '',
      })
    } else {
      setEditingConfig(null)
      setFormData({
        nombre: '',
        ambiente: 'pruebas',
        usuario: '',
        password: '',
        url_timbrado: 'https://ws.multifacturas.com/api/',
        url_cancelacion: 'https://ws.multifacturas.com/api/',
        url_estatus: 'https://ws.multifacturas.com/api/',
        activo: true,
        notas: '',
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingConfig(null)
    setFormData({
      nombre: '',
      ambiente: 'pruebas',
      usuario: '',
      password: '',
      url_timbrado: 'https://ws.multifacturas.com/api/',
      url_cancelacion: 'https://ws.multifacturas.com/api/',
      url_estatus: 'https://ws.multifacturas.com/api/',
      activo: true,
      notas: '',
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const dataToSend = {
      nombre: formData.nombre,
      ambiente: formData.ambiente,
      usuario: formData.usuario,
      url_timbrado: formData.url_timbrado,
      url_cancelacion: formData.url_cancelacion || null,
      url_estatus: formData.url_estatus || null,
      activo: formData.activo,
      notas: formData.notas || '',
    }

    // Solo incluir contraseña si se está cambiando
    if (formData.password) {
      dataToSend.password = formData.password
    }

    saveMutation.mutate(dataToSend)
  }

  const handleDelete = (id) => {
    if (window.confirm('¿Está seguro de eliminar esta configuración global? Esto desactivará todas las configuraciones por cliente que la usen.')) {
      deleteMutation.mutate(id)
    }
  }

  const handleValidate = (id) => {
    setValidatingId(id)
    validateMutation.mutate(id)
  }

  if (!isSuperAdmin) {
    return (
      <Alert variant="warning">
        Solo los superadministradores pueden acceder a las configuraciones PAC globales.
      </Alert>
    )
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Configuraciones PAC Globales</h4>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-circle me-2"></i>
            Nueva Configuración Global
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Ambiente</Form.Label>
                <Form.Select
                  value={filterAmbiente}
                  onChange={(e) => setFilterAmbiente(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="pruebas">Pruebas</option>
                  <option value="produccion">Producción</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Ambiente</th>
                  <th>Usuario</th>
                  <th>URL Timbrado</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No se encontraron configuraciones globales
                    </td>
                  </tr>
                ) : (
                  configs.map(config => (
                    <tr key={config.id}>
                      <td>{config.nombre}</td>
                      <td>
                        <Badge bg={config.ambiente === 'produccion' ? 'danger' : 'warning'}>
                          {config.ambiente_display || (config.ambiente === 'produccion' ? 'Producción' : 'Pruebas')}
                        </Badge>
                      </td>
                      <td>{config.usuario}</td>
                      <td>
                        <small className="text-muted">{config.url_timbrado}</small>
                      </td>
                      <td>
                        <Badge bg={config.activo ? 'success' : 'secondary'}>
                          {config.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleValidate(config.id)}
                            disabled={validatingId === config.id}
                            title="Validar credenciales"
                            style={{ minWidth: '100px' }}
                          >
                            {validatingId === config.id ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                <span>Validando...</span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-1"></i>
                                <span>Validar</span>
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenModal(config)}
                            title="Editar"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            <span>Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config.id)}
                            title="Eliminar"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingConfig ? 'Editar Configuración Global' : 'Nueva Configuración Global'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="info">
              <strong>Configuración Global:</strong> Esta configuración será utilizada por múltiples clientes.
              Solo puede haber una configuración activa con el mismo nombre y ambiente.
            </Alert>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de Configuración *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Multifacturas Producción"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ambiente *</Form.Label>
                  <Form.Select
                    value={formData.ambiente}
                    onChange={(e) => setFormData({ ...formData, ambiente: e.target.value })}
                    required
                  >
                    <option value="pruebas">Pruebas</option>
                    <option value="produccion">Producción</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Usuario Multifacturas *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.usuario}
                    onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contraseña {editingConfig && '(dejar vacío para no cambiar)'}</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingConfig}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>URL Timbrado *</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.url_timbrado}
                    onChange={(e) => setFormData({ ...formData, url_timbrado: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">URL base de Multifacturas (https://ws.multifacturas.com/api/)</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>URL Cancelación</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.url_cancelacion}
                    onChange={(e) => setFormData({ ...formData, url_cancelacion: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>URL Estatus</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.url_estatus}
                    onChange={(e) => setFormData({ ...formData, url_estatus: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Si se desactiva, todas las configuraciones por cliente que usen esta configuración global también se desactivarán.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    placeholder="Notas adicionales sobre esta configuración..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isLoading}>
              {saveMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default PACGlobalConfigurations
